import { first, map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { BaseParamsService } from '../base-params.service';
import { SessionService } from '../session/session.service';
import { SettingsService } from '../settings/settings.service';
import { Settings } from '../_models/settings';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '@app/core/store/reducers';
import { LoadBasicMember } from '@app/core/store/member/member-actions';
import { REDIRECT_QUERY_PARAM } from '@app/core/consts';

@Injectable()
export class SignedInOrAutoLoginGuard implements CanActivate, CanActivateChild {
  private router = inject(Router);
  private location = inject(Location);
  private sessionService = inject(SessionService);
  private baseParamsService = inject(BaseParamsService);
  private settingsService = inject(SettingsService);
  private store = inject<Store<fromRoot.State>>(Store);

  private offerUseAuthorization = true;

  public canActivate(next: ActivatedRouteSnapshot): Observable<boolean> {
    this.baseParamsService.parseParamsWithAutologgable(next);
    return this.onSettingsFetched(this.settingsService.settings);
  }

  public canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<boolean> {
    return this.canActivate(childRoute);
  }

  public navigateToMainPage(): void {
    this.router.navigate(this.mainRouteLink());
  }

  public isAuthorizedToUseOffer(): boolean {
    return this.offerUseAuthorization;
  }

  private onSettingsFetched(settings: Settings): Observable<boolean> {
    this.handleBasicMemberLoad();
    return this.baseParamsService.hasAutologinParams() ? of(true) : this.normalLoginStrategy(settings);
  }

  private handleBasicMemberLoad(): void {
    this.store.pipe(
      select(fromRoot.selectMember),
      first(),
    ).subscribe((member) => {
      if (!member.isBasicMemberFetched) { this.store.dispatch(new LoadBasicMember()); }
    });
  }

  private normalLoginStrategy(settings: Settings): Observable<boolean> {
    return this.sessionService.isBasicallyAuthenticated().pipe(
      map((isAuthenticated) => {
        if (isAuthenticated) {
          this.setOfferUseAuthorization(true);
          return true;
        }
        if (settings.coupon_preview_enabled) {
          this.setOfferUseAuthorization(false);
          return true;
        }

        this.navigateToSignUpPage();
        return false;
      }),
    );
  }

  private navigateToSignUpPage(): void {
    this.router.navigate(this.mainRouteLink(), { queryParams: { [REDIRECT_QUERY_PARAM]: this.location.path() } });
  }

  private setOfferUseAuthorization(authorized: boolean): void {
    this.offerUseAuthorization = authorized;
  }

  private mainRouteLink(): string[] {
    return [this.baseParamsService.getRootURI()];
  }
}
